import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PageHeader, FilterBar, DataTable, Modal, Badge, EmptyState, FormSection, Tabs } from '../components/ui/SharedUI';
import { demoRepository } from '../repository/demoRepository';
import { calculations } from '../utils/calculations';
import { useFeedback } from '../components/ui/FeedbackUI';
import SafeIcon from '../common/SafeIcon';
import { pocketbaseService } from '../services/pocketbaseService';
import SmartDateInput from '../components/ui/SmartDateInput';

const statusDisplayMap = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  waiting_on_agent: 'Waiting on Agent',
  waiting_on_owner: 'Waiting on Owner',
  waiting_on_tenant: 'Waiting on Tenant',
  waiting_on_contractor: 'Waiting on Contractor',
  pending_approval: 'Pending Approval',
  resolved: 'Resolved',
  closed: 'Closed',
  archived: 'Archived'
};

const priorityDisplayMap = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent'
};

const responsibleDisplayMap = {
  owner: 'Owner',
  tenant: 'Tenant',
  agent: 'Agent',
  contractor: 'Contractor',
  other: 'Other'
};

const displayStatus = (val) => statusDisplayMap[val] || val || 'Open';
const displayPriority = (val) => priorityDisplayMap[val] || val || 'Normal';
const displayResponsible = (val) => responsibleDisplayMap[val] || val || 'Owner';

const statusBackendOptions = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' }
];

const priorityBackendOptions = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const responsibleBackendOptions = [
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'agent', label: 'Agent' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' }
];

const categories = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'agent_follow_up', label: 'Agent Follow-up' },
  { value: 'tenant_issue', label: 'Tenant Issue' },
  { value: 'owner_decision', label: 'Owner Decision' },
  { value: 'document_request', label: 'Document Request' },
  { value: 'finance', label: 'Finance' },
  { value: 'other', label: 'Other' }
];

const OpenItemsPage = () => {
  const { showToast, showConfirm, showStagedMessages } = useFeedback();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [propertyFilter, setPropertyFilter] = useState('All');
  const [responsibleFilter, setResponsibleFilter] = useState('All');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handleDataRefreshed = () => setRefreshTrigger(prev => prev + 1);
    window.addEventListener('mpp:data-refreshed', handleDataRefreshed);
    return () => window.removeEventListener('mpp:data-refreshed', handleDataRefreshed);
  }, []);

  const allOpenItems = useMemo(() => {
    return demoRepository.getAllOpenItems();
  }, [refreshTrigger]);

  const properties = useMemo(() => {
    return demoRepository.getAllProperties().filter(p => p.status !== 'Archived');
  }, []);

  const filteredItems = useMemo(() => {
    return allOpenItems.filter(item => {
      if (categoryFilter !== 'All' && item.category !== categoryFilter) return false;
      if (statusFilter !== 'All' && item.status !== statusFilter) return false;
      if (priorityFilter !== 'All' && item.priority !== priorityFilter) return false;
      if (propertyFilter !== 'All' && item.propertyId !== propertyFilter) return false;
      if (responsibleFilter !== 'All' && item.responsibleParty !== responsibleFilter) return false;

      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const property = item.propertyId ? demoRepository.getPropertyById(item.propertyId) : null;
        return (
          item.title?.toLowerCase().includes(lower) ||
          item.description?.toLowerCase().includes(lower) ||
          item.notes?.toLowerCase().includes(lower) ||
          item.nextAction?.toLowerCase().includes(lower) ||
          property?.name?.toLowerCase().includes(lower)
        );
      }
      return true;
    });
  }, [allOpenItems, searchTerm, categoryFilter, statusFilter, priorityFilter, propertyFilter, responsibleFilter]);

  const statistics = useMemo(() => {
    const active = allOpenItems.filter(i => !['resolved', 'closed', 'archived'].includes(i.status));
    const byProperty = {};
    const byResponsible = {};
    
    const overdue = active.filter(i => {
      const followUp = i.followUpDate ? new Date(i.followUpDate) : null;
      return followUp && followUp < new Date();
    });

    active.forEach(item => {
      const prop = demoRepository.getPropertyById(item.propertyId);
      const propName = prop?.name || 'Unknown';
      const resp = item.responsibleParty || 'unassigned';
      
      byProperty[propName] = (byProperty[propName] || 0) + 1;
      byResponsible[resp] = (byResponsible[resp] || 0) + 1;
    });

    return {
      total: allOpenItems.length,
      active: active.length,
      resolved: allOpenItems.filter(i => ['resolved', 'closed'].includes(i.status)).length,
      overdue: overdue.length,
      byProperty,
      byResponsible
    };
  }, [allOpenItems]);

  const handleAddItem = useCallback(async (data) => {
    try {
      let result;
      if (editingItem) {
        result = await demoRepository.updateOpenItem(editingItem.id, data);
      } else {
        result = await demoRepository.addOpenItem(data);
      }

      if (!result?.success) {
        showToast(result?.error || 'Open item save failed.', 'error');
        return;
      }
      
      const mode = result.mode;
      showToast(
        mode === 'pocketbase' ? 'Open item saved to PocketBase.' : 'Open item saved to demo data.',
        'success'
      );
      
      setIsAddModalOpen(false);
      setEditingItem(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      showToast(error?.message || 'Open item save failed.', 'error');
    }
  }, [editingItem, showToast]);

  const handleEditItem = useCallback((item) => {
    setEditingItem(item);
    setIsAddModalOpen(true);
  }, []);

  const handleViewTimeline = useCallback((item) => {
    setSelectedItem(item);
    setIsTimelineModalOpen(true);
  }, []);

  const handleOpenEmailComposer = useCallback((item) => {
    setSelectedItem(item);
    setIsEmailModalOpen(true);
  }, []);

  const handleCloseItem = useCallback((item) => {
    showConfirm({
      title: 'Close Open Item?',
      message: `Are you sure you want to close "${item.title}"? This will mark it as resolved.`,
      confirmText: 'Close Item',
      onConfirm: async () => {
        try {
          const result = await demoRepository.updateOpenItem(item.id, { 
            status: 'resolved',
            resolvedDate: Date.now()
          });
          if (result?.success) {
            showToast('Item closed', 'success');
            setRefreshTrigger(prev => prev + 1);
          } else {
            showToast(result?.error || 'Close failed.', 'error');
          }
        } catch (error) {
          showToast(error?.message || 'Close failed.', 'error');
        }
      }
    });
  }, [showConfirm, showToast]);

  const handleArchiveItem = useCallback((item) => {
    showConfirm({
      title: 'Archive Open Item?',
      message: `Are you sure you want to archive "${item.title}"?`,
      confirmText: 'Archive',
      isDestructive: true,
      onConfirm: async () => {
        try {
          const result = await demoRepository.updateOpenItem(item.id, { status: 'archived' });
          if (result?.success) {
            showToast('Item archived', 'info');
            setRefreshTrigger(prev => prev + 1);
          } else {
            showToast(result?.error || 'Archive failed.', 'error');
          }
        } catch (error) {
          showToast(error?.message || 'Archive failed.', 'error');
        }
      }
    });
  }, [showConfirm, showToast]);

  const handleDuplicateItem = useCallback(async (item) => {
    const duplicate = {
      ...item,
      id: undefined,
      title: `${item.title} (Copy)`,
      status: 'open',
      openedDate: Date.now(),
      resolvedDate: null
    };
    
    try {
      const result = await demoRepository.addOpenItem(duplicate);
      if (result?.success) {
        showToast('Item duplicated', 'success');
        setRefreshTrigger(prev => prev + 1);
      } else {
        showToast(result?.error || 'Duplicate failed.', 'error');
      }
    } catch (error) {
      showToast(error?.message || 'Duplicate failed.', 'error');
    }
  }, [showToast]);

  const handleStatusChange = useCallback(async (item, newStatus) => {
    try {
      const result = await demoRepository.updateOpenItem(item.id, { status: newStatus });
      if (result?.success) {
        showToast(`Status changed to ${displayStatus(newStatus)}`, 'success');
        setRefreshTrigger(prev => prev + 1);
      } else {
        showToast(result?.error || 'Status change failed.', 'error');
      }
    } catch (error) {
      showToast(error?.message || 'Status change failed.', 'error');
    }
  }, [showToast]);

  const handlePriorityChange = useCallback(async (item, newPriority) => {
    try {
      const result = await demoRepository.updateOpenItem(item.id, { priority: newPriority });
      if (result?.success) {
        showToast(`Priority changed to ${displayPriority(newPriority)}`, 'success');
        setRefreshTrigger(prev => prev + 1);
      } else {
        showToast(result?.error || 'Priority change failed.', 'error');
      }
    } catch (error) {
      showToast(error?.message || 'Priority change failed.', 'error');
    }
  }, [showToast]);

  const columns = [
    'Title', 'Property', 'Category', 'Priority', 'Status', 'Responsible', 'Age', 'Follow-up', 'Actions'
  ];

  const renderRow = (item) => {
    const property = item.propertyId ? demoRepository.getPropertyById(item.propertyId) : null;
    const age = calculations.calculateOpenItemAge(item.openedDate);
    
    const isOverdue = item.followUpDate && 
      new Date(item.followUpDate) < new Date() && 
      item.status !== 'resolved' && 
      item.status !== 'archived';
      
    const daysToFollowUp = item.followUpDate ? calculations.calculateDaysToExpiry(item.followUpDate) : null;

    const statusColors = {
      open: 'overdue',
      in_progress: 'pending',
      waiting: 'pending',
      waiting_on_agent: 'pending',
      waiting_on_owner: 'pending',
      waiting_on_tenant: 'pending',
      waiting_on_contractor: 'pending',
      pending_approval: 'pending',
      resolved: 'active',
      closed: 'default',
      archived: 'default'
    };

    const priorityColors = {
      low: 'default',
      normal: 'default',
      high: 'overdue',
      urgent: 'overdue'
    };

    return [
      <div key={`title-${item.id}`}>
        <div className="font-medium text-slate-900">{item.title}</div>
        {(item.description || item.notes) && 
          <div className="text-xs text-slate-500 truncate max-w-[200px]">{item.description || item.notes}</div>
        }
      </div>,
      property?.name || '-',
      <Badge key={`cat-${item.id}`} label={item.category || 'other'} status="default" />,
      <Badge key={`pri-${item.id}`} label={displayPriority(item.priority)} status={priorityColors[item.priority] || 'default'} />,
      <Badge key={`stat-${item.id}`} label={displayStatus(item.status)} status={statusColors[item.status] || 'default'} />,
      displayResponsible(item.responsibleParty) || '-',
      <div key={`age-${item.id}`}>
        <div className="font-medium">{age} days</div>
        {isOverdue && <div className="text-xs text-red-600">Overdue</div>}
      </div>,
      item.followUpDate ? (
        <div key={`fup-${item.id}`} className={isOverdue ? 'text-red-600 font-medium' : ''}>
          {calculations.formatDateAU(item.followUpDate)}
          {daysToFollowUp !== null && daysToFollowUp > 0 && 
            <div className="text-xs text-slate-500">{daysToFollowUp} days</div>
          }
        </div>
      ) : '-',
      <div key={`acts-${item.id}`} className="flex flex-wrap gap-1 items-center">
        <button type="button" onClick={(e) => { e.stopPropagation(); handleEditItem(item); }} className="text-amber-600 hover:underline text-xs font-medium">Edit</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); handleViewTimeline(item); }} className="text-blue-600 hover:underline text-xs font-medium">Notes</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); handleOpenEmailComposer(item); }} className="text-indigo-600 hover:underline text-xs font-medium">Email</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); handleCloseItem(item); }} className="text-emerald-600 hover:underline text-xs font-medium">Close</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); handleArchiveItem(item); }} className="text-red-600 hover:underline text-xs font-medium">Archive</button>
      </div>
    ];
  };

  return (
    <div className="animate-fade-in">
      <PageHeader 
        title="Open Items" 
        subtitle="Action register for maintenance, agent follow-ups, and tasks."
        actionLabel="New Open Item"
        onAction={() => { setEditingItem(null); setIsAddModalOpen(true); }}
      />

      <FormSection title="Action Register Summary" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-sm text-slate-600 mb-1">Total Items</div>
            <div className="text-2xl font-bold text-slate-900">{statistics.total}</div>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="text-sm text-amber-700 mb-1">Active</div>
            <div className="text-2xl font-bold text-amber-900">{statistics.active}</div>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-sm text-emerald-700 mb-1">Resolved</div>
            <div className="text-2xl font-bold text-emerald-900">{statistics.resolved}</div>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-sm text-red-700 mb-1">Overdue</div>
            <div className="text-2xl font-bold text-red-900">{statistics.overdue}</div>
          </div>
        </div>
      </FormSection>

      <FilterBar 
        onSearch={setSearchTerm} 
        onClear={() => { 
          setSearchTerm(''); 
          setCategoryFilter('All');
          setStatusFilter('All');
          setPriorityFilter('All');
          setPropertyFilter('All');
          setResponsibleFilter('All');
        }}
      >
        <select 
          value={categoryFilter} 
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Categories</option>
          {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
        </select>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Statuses</option>
          {statusBackendOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select 
          value={priorityFilter} 
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Priorities</option>
          {priorityBackendOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select 
          value={propertyFilter} 
          onChange={(e) => setPropertyFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select 
          value={responsibleFilter} 
          onChange={(e) => setResponsibleFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Responsible</option>
          {responsibleBackendOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </FilterBar>

      {filteredItems.length > 0 ? (
        <DataTable 
          columns={columns} 
          data={filteredItems} 
          renderRow={renderRow} 
          emptyMessage="No open items found matching your criteria."
        />
      ) : (
        <EmptyState 
          title="No Open Items" 
          description="Create open items to track maintenance, agent follow-ups, and action items for your properties."
          actionLabel="New Open Item"
          onAction={() => { setEditingItem(null); setIsAddModalOpen(true); }}
          icon="AlertCircle"
        />
      )}

      <Modal 
        isOpen={isAddModalOpen} 
        onClose={() => { setIsAddModalOpen(false); setEditingItem(null); }} 
        title={editingItem ? 'Edit Open Item' : 'New Open Item'}
        maxWidth="max-w-5xl"
      >
        <OpenItemForm 
          initialData={editingItem} 
          properties={properties}
          categories={categories}
          statuses={statusBackendOptions}
          priorities={priorityBackendOptions}
          responsibleParties={responsibleBackendOptions}
          onSubmit={handleAddItem}
          onCancel={() => { setIsAddModalOpen(false); setEditingItem(null); }}
        />
      </Modal>

      <Modal 
        isOpen={isTimelineModalOpen} 
        onClose={() => { setIsTimelineModalOpen(false); setSelectedItem(null); }} 
        title={selectedItem ? `Notes / Timeline: ${selectedItem.title}` : 'Notes / Timeline'}
        maxWidth="max-w-5xl"
      >
        {selectedItem && (
          <CommunicationTimeline 
            item={selectedItem} 
            onItemUpdated={(updatedItem) => {
              setSelectedItem(updatedItem);
              setRefreshTrigger(prev => prev + 1);
            }} 
            onOpenEmail={() => { setIsTimelineModalOpen(false); setIsEmailModalOpen(true); }} 
          />
        )}
      </Modal>

      <Modal 
        isOpen={isEmailModalOpen} 
        onClose={() => { setIsEmailModalOpen(false); setSelectedItem(null); }} 
        title="Email Composer"
        maxWidth="max-w-4xl"
      >
        {selectedItem && (
          <EmailComposer 
            openItem={selectedItem}
            properties={properties}
            onSend={(data) => {
              const stages = [
                'Preparing message...',
                'Validating recipients...',
                'Checking attachments...',
                'Creating email payload...',
                'Sending via secure relay...',
                'Logging communication...',
                'Email sent and added to timeline'
              ];
              
              let currentStep = 0;
              
              showStagedMessages(stages, async () => {
                const progressCallback = (step) => {
                  currentStep = step;
                };
                
                const result = await pocketbaseService.email.send(data, progressCallback);
                
                if (result.success) {
                  await pocketbaseService.email.logEmail(result, selectedItem.id);
                  showToast('Email sent successfully', 'success');
                  setIsEmailModalOpen(false);
                  setSelectedItem(null);
                  setRefreshTrigger(prev => prev + 1);
                } else {
                  showToast(`Failed: ${result.error}`, 'error');
                }
              });
            }}
            onCancel={() => { setIsEmailModalOpen(false); setSelectedItem(null); }}
          />
        )}
      </Modal>
    </div>
  );
};

const OpenItemForm = ({ 
  initialData, 
  properties, 
  categories, 
  statuses, 
  priorities, 
  responsibleParties, 
  onSubmit, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    propertyId: initialData?.propertyId || '',
    spaceId: initialData?.spaceId || '',
    tenantId: initialData?.tenantId || '',
    leaseId: initialData?.leaseId || '',
    transactionId: initialData?.transactionId || '',
    inspectionId: initialData?.inspectionId || '',
    insuranceId: initialData?.insuranceId || '',
    loanId: initialData?.loanId || '',
    documentId: initialData?.documentId || '',
    
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || 'maintenance',
    status: initialData?.status || 'open',
    priority: initialData?.priority || 'normal',
    responsibleParty: initialData?.responsibleParty || 'owner',
    
    openedDate: initialData?.openedDate ? new Date(initialData.openedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    followUpDate: initialData?.followUpDate ? new Date(initialData.followUpDate).toISOString().split('T')[0] : '',
    lastUpdate: initialData?.lastUpdate ? new Date(initialData.lastUpdate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    
    nextAction: initialData?.nextAction || '',
    notes: initialData?.notes || '',
    closureOutcome: initialData?.closureOutcome || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        openedDate: new Date(formData.openedDate).getTime(),
        followUpDate: formData.followUpDate ? new Date(formData.followUpDate).getTime() : null,
        dueDate: formData.followUpDate ? new Date(formData.followUpDate).getTime() : null,
        lastUpdate: Date.now()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const spaces = formData.propertyId ? demoRepository.getSpacesByProperty(formData.propertyId) : [];
  const leases = formData.propertyId ? demoRepository.getCurrentLeasesByProperty(formData.propertyId) : [];

  const inputClass = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className={labelClass}>Title *</label>
          <input 
            type="text" 
            name="title" 
            value={formData.title} 
            onChange={handleChange} 
            className={inputClass} 
            placeholder="Brief title for this item"
            required 
          />
        </div>
        
        <div className="md:col-span-2">
          <label className={labelClass}>Issue / Action Description</label>
          <textarea 
            name="description" 
            value={formData.description} 
            onChange={handleChange} 
            className={inputClass} 
            rows="3"
            placeholder="Detailed description of the issue or action required"
          />
        </div>

        <div>
          <label className={labelClass}>Property</label>
          <select name="propertyId" value={formData.propertyId} onChange={handleChange} className={inputClass}>
            <option value="">Select property...</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {formData.propertyId && (
          <div>
            <label className={labelClass}>Room/Space</label>
            <select name="spaceId" value={formData.spaceId} onChange={handleChange} className={inputClass}>
              <option value="">Select space...</option>
              {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className={labelClass}>Category *</label>
          <select name="category" value={formData.category} onChange={handleChange} className={inputClass} required>
            {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>Priority</label>
          <select name="priority" value={formData.priority} onChange={handleChange} className={inputClass}>
            {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>Status *</label>
          <select name="status" value={formData.status} onChange={handleChange} className={inputClass} required>
            {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>Responsible Party</label>
          <select name="responsibleParty" value={formData.responsibleParty} onChange={handleChange} className={inputClass}>
            {responsibleParties.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>Opened Date</label>
          <SmartDateInput
            name="openedDate"
            value={formData.openedDate}
            onChange={handleChange}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Follow-up Date</label>
          <SmartDateInput
            name="followUpDate"
            value={formData.followUpDate}
            onChange={handleChange}
            className={inputClass}
            baseDate={formData.openedDate}
            baseLabel="the opened date"
          />
        </div>

        <div>
          <label className={labelClass}>Last Update</label>
          <SmartDateInput
            name="lastUpdate"
            value={formData.lastUpdate}
            onChange={handleChange}
            className={inputClass}
            baseDate={formData.openedDate}
            baseLabel="the opened date"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelClass}>Next Action Required</label>
          <input 
            type="text" 
            name="nextAction" 
            value={formData.nextAction} 
            onChange={handleChange} 
            className={inputClass} 
            placeholder="What needs to happen next?"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelClass}>Internal Notes / Follow-up Notes</label>
          <textarea 
            name="notes" 
            value={formData.notes} 
            onChange={handleChange} 
            className={inputClass} 
            rows="3"
            placeholder="Additional notes and details"
          />
        </div>

        {(formData.status === 'resolved' || formData.status === 'closed') && (
          <div className="md:col-span-2">
            <label className={labelClass}>Closure Outcome</label>
            <textarea 
              name="closureOutcome" 
              value={formData.closureOutcome} 
              onChange={handleChange} 
              className={inputClass} 
              rows="2"
              placeholder="How was this issue resolved?"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button 
          type="button" 
          onClick={onCancel} 
          className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : (initialData ? 'Update Item' : 'Create Item')}
        </button>
      </div>
    </form>
  );
};

const CommunicationTimeline = ({ item, onItemUpdated, onOpenEmail }) => {
  const [noteText, setNoteText] = useState('');
  const { showToast } = useFeedback();

  const communications = useMemo(() => {
    return demoRepository.getCommunicationLogsByOpenItem(item.id).sort((a, b) => b.datetime - a.datetime);
  }, [item.id]);

  const timelineNotes = item.metadata?.timeline || [];

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    const newEntry = {
      id: `timeline_${Date.now()}`,
      timestamp: Date.now(),
      type: 'note',
      text: noteText.trim(),
      createdBy: 'AM',
      statusSnapshot: item.status,
      prioritySnapshot: item.priority
    };

    const existingTimeline = item.metadata?.timeline || [];
    const updatedMetadata = {
      ...(item.metadata || {}),
      timeline: [newEntry, ...existingTimeline]
    };

    const result = await demoRepository.updateOpenItem(item.id, { metadata: updatedMetadata });
    
    if (result?.success) {
      showToast('Timestamped note added.', 'success');
      onItemUpdated(result.record || { ...item, metadata: updatedMetadata });
      setNoteText('');
    } else {
      showToast(result?.error || 'Note add failed.', 'error');
    }
  };

  const getTypeIcon = (type) => {
    const icons = { Note: 'FileText', Email: 'Mail', Phone: 'Phone', SMS: 'MessageSquare', Meeting: 'Calendar', StatusChange: 'RefreshCw', Document: 'File' };
    return icons[type] || 'Circle';
  };
  
  const getTypeColor = (type) => {
    const colors = { Note: 'text-slate-600', Email: 'text-blue-600', Phone: 'text-green-600', SMS: 'text-amber-600', Meeting: 'text-purple-600', StatusChange: 'text-indigo-600', Document: 'text-red-600' };
    return colors[type] || 'text-slate-600';
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-semibold text-slate-800">Add Timestamped Note</label>
          <button type="button" onClick={onOpenEmail} className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg">Compose Email</button>
        </div>
        <div className="flex gap-2 items-start">
          <textarea 
            value={noteText} 
            onChange={(e) => setNoteText(e.target.value)} 
            placeholder="Add a timestamped note..." 
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[80px]"
          />
          <button 
            type="button" 
            onClick={handleAddNote} 
            disabled={!noteText.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50 whitespace-nowrap"
          >
            Add Note
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Open Item Notes</h3>
        <div className="space-y-3">
          {timelineNotes.length > 0 ? (
            timelineNotes.map((entry, idx) => (
              <div key={entry.id || idx} className="p-3 bg-white border border-slate-200 rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <div className="text-xs text-slate-500">{calculations.formatDateAU(entry.timestamp)}</div>
                  <Badge label={entry.type} status="default" />
                </div>
                <div className="text-sm text-slate-800 font-medium whitespace-pre-wrap">{entry.text}</div>
                <div className="text-xs text-slate-400 mt-1">
                  By: {entry.createdBy}
                  {entry.statusSnapshot && ` | Status: ${entry.statusSnapshot}`}
                  {entry.prioritySnapshot && ` | Priority: ${entry.prioritySnapshot}`}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">No timestamped notes yet.</div>
          )}
        </div>
      </div>

      {communications.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <h3 className="text-md font-semibold text-slate-800 mb-4">Communication Logs</h3>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>
            <div className="space-y-4">
              {communications.map((comm, idx) => (
                <div key={idx} className="relative pl-16">
                  <div className={`absolute left-0 top-1 w-12 h-12 rounded-full bg-white border-2 border-amber-500 flex items-center justify-center z-10`}>
                    <SafeIcon icon={getTypeIcon(comm.type)} className={`${getTypeColor(comm.type)} text-lg`} />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-900">{comm.summary || comm.type}</h4>
                        {comm.details && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{comm.details}</p>}
                        {comm.contactName && <p className="text-xs text-slate-500 mt-1">Contact: {comm.contactName}</p>}
                        {comm.status && <Badge label={comm.status} status="default" />}
                      </div>
                      <Badge label={comm.type} status="default" />
                    </div>
                    <div className="text-xs text-slate-500">{calculations.formatDateAU(comm.datetime)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailComposer = ({ openItem, properties, onSend, onCancel }) => {
  const [formData, setFormData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: `Re: ${openItem.title}`,
    body: '',
    notes: '',
    attachmentMode: 'link',
    selectedDocuments: []
  });
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const documents = openItem.propertyId ? demoRepository.getDocumentsByProperty(openItem.propertyId).filter(d => d.status === 'Active') : [];
  const emailConfig = pocketbaseService.email.getConfig();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentToggle = (docId) => {
    setFormData(prev => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.includes(docId) 
        ? prev.selectedDocuments.filter(id => id !== docId)
        : [...prev.selectedDocuments, docId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setSendError(null);

    const emailData = {
      ...formData,
      openItemId: openItem.id,
      linkedRecordType: 'open_item',
      linkedRecordId: openItem.id,
      documents: formData.selectedDocuments.map(id => documents.find(d => d.id === id))
    };

    onSend(emailData);
    setIsSending(false);
  };

  const inputClass = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!emailConfig.isConfigured && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <div className="flex items-start gap-3">
            <SafeIcon icon="Info" className="text-amber-600 text-xl flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-amber-800">Email Relay Not Configured</h4>
              <p className="text-sm text-amber-700 mt-1">Set VITE_GAS_RELAY_URL environment variable to enable email sending.</p>
            </div>
          </div>
        </div>
      )}

      {sendError && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-start gap-3">
            <SafeIcon icon="AlertCircle" className="text-red-600 text-xl flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800">Sending Failed</h4>
              <p className="text-sm text-red-700 mt-1">{sendError}</p>
            </div>
            <button type="button" onClick={() => setSendError(null)} className="text-red-600 hover:text-red-800"><SafeIcon icon="X" className="text-lg" /></button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className={labelClass}>To *</label>
          <input type="email" name="to" value={formData.to} onChange={handleChange} className={inputClass} placeholder="recipient@email.com" required />
        </div>
        
        <div>
          <label className={labelClass}>CC {emailConfig.defaultCC && <span className="text-xs text-slate-500">(Default: {emailConfig.defaultCC})</span>}</label>
          <input type="text" name="cc" value={formData.cc} onChange={handleChange} className={inputClass} placeholder="cc@email.com" />
        </div>

        <div>
          <label className={labelClass}>BCC {emailConfig.defaultBCC && <span className="text-xs text-slate-500">(Default: {emailConfig.defaultBCC})</span>}</label>
          <input type="text" name="bcc" value={formData.bcc} onChange={handleChange} className={inputClass} placeholder="bcc@email.com" />
        </div>

        <div>
          <label className={labelClass}>Subject *</label>
          <input type="text" name="subject" value={formData.subject} onChange={handleChange} className={inputClass} required />
        </div>

        <div>
          <label className={labelClass}>Message *</label>
          <textarea name="body" value={formData.body} onChange={handleChange} className={`${inputClass} min-h-[200px]`} placeholder="Compose your message..." required />
        </div>

        <div>
          <label className={labelClass}>Internal Notes</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} className={inputClass} rows="2" placeholder="Internal notes about this email (not sent)" />
        </div>

        {documents.length > 0 && (
          <div>
            <label className={labelClass}>Attachments</label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {documents.map(doc => {
                const isSendable = pocketbaseService.email.isDocumentSendable(doc);
                return (
                  <label key={doc.id} className={`flex items-center gap-2 text-sm p-1 rounded ${!isSendable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                    <input 
                      type="checkbox" 
                      checked={formData.selectedDocuments.includes(doc.id)}
                      onChange={() => isSendable && handleDocumentToggle(doc.id)}
                      className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      disabled={!isSendable}
                    />
                    <span className={`text-slate-700 ${!isSendable ? 'text-slate-400' : ''}`}>{doc.fileName}</span>
                    <Badge label={doc.fileType} status="default" />
                    {!isSendable && <Badge label="Not sendable" status="default" />}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={isSending} className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${isSending ? 'bg-slate-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-800'}`}>
          {isSending ? 'Sending...' : 'Send Email'}
        </button>
      </div>
    </form>
  );
};

export default OpenItemsPage;